/**
 * Advogado CNA Integration Library
 * Handles lawyer information retrieval and processing
 */

/**
 * Extract lawyers from envolvidos list
 * @param {Array} envolvidos - List of involved parties
 * @returns {Array} List of lawyers with their information
 */
export const extractAdvogadosFromEnvolvidos = (envolvidos) => {
  if (!envolvidos || !Array.isArray(envolvidos)) return [];
  
  return envolvidos.filter(env => 
    env.polo === 'ADVOGADO' || 
    env.tipo_normalizado === 'Advogado' ||
    (env.tipo && env.tipo.toLowerCase().includes('advogado'))
  );
};

/**
 * Format OAB number for display
 * @param {Array} oabs - Array of OAB registrations
 * @returns {String} Formatted OAB string
 */
export const formatOAB = (oabs) => {
  if (!oabs || !Array.isArray(oabs) || oabs.length === 0) return null;
  
  return oabs.map(oab => 
    `${oab.uf} ${oab.numero}${oab.tipo ? ` (${oab.tipo})` : ''}`
  ).join(', ');
};

/**
 * Format CPF for display
 * @param {String} cpf - CPF number
 * @returns {String} Formatted CPF
 */
export const formatCPF = (cpf) => {
  if (!cpf) return null;
  const cleaned = cpf.replace(/\D/g, '');
  if (cleaned.length !== 11) return cpf;
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
};

/**
 * Prepare lawyer data for CNA search
 * @param {Object} advogado - Lawyer information
 * @param {String} estado - State code (e.g., 'BA')
 * @returns {Object} Prepared data for CNA API
 */
export const prepareAdvogadoForCNA = (advogado, estado) => {
  const oabInfo = advogado.oabs && advogado.oabs.length > 0 ? advogado.oabs[0] : null;
  
  return {
    nome: advogado.nome,
    cpf: advogado.cpf,
    oab: oabInfo ? {
      numero: oabInfo.numero,
      uf: oabInfo.uf || estado,
      tipo: oabInfo.tipo
    } : null,
    estado: estado,
    tipo_pessoa: advogado.tipo_pessoa,
    quantidade_processos: advogado.quantidade_processos,
    // Original data for reference
    _original: advogado
  };
};

/**
 * Fetch lawyer data from backend (which integrates with DirectData API)
 * 
 * @param {Object} advogadoData - Prepared lawyer data
 * @returns {Promise<Object>} Response data
 */
export const fetchAdvogadoFromCNA = async (advogadoData) => {
  if (!advogadoData.cpf) {
    throw new Error('CPF é obrigatório para consulta');
  }

  // Clean CPF (remove formatting)
  const cpfClean = advogadoData.cpf.replace(/\D/g, '');

  try {
    // Call our backend API that handles DirectData integration
    const API_BASE_URL = process.env.NODE_ENV === 'production'
      ? process.env.REACT_APP_VERCEL_API_URL
      : process.env.REACT_APP_LOCAL_API_URL;
    
    // Get pessoa física data from DirectData API
    const response = await fetch(`${API_BASE_URL}api/pessoa-fisica/${cpfClean}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Erro na consulta: ${response.status}`);
    }

    const pessoaFisicaData = await response.json();
    
    // Transform the DirectData response to match our expected format
    if (pessoaFisicaData.success && pessoaFisicaData.data) {
      const data = pessoaFisicaData.data;
      
      return {
        success: true,
        data: {
          nome_completo: data.nome,
          cpf: data.cpf,
          data_nascimento: data.dataNascimento,
          sexo: data.sexo,
          nome_mae: data.nomeMae,
          idade: data.idade,
          signo: data.signo,
          // Map OAB inscriptions if available
          inscricoes: advogadoData.oab ? [{
            numero: advogadoData.oab.numero,
            uf: advogadoData.oab.uf,
            tipo: advogadoData.oab.tipo,
            situacao: 'ATIVO',
            data_inscricao: null
          }] : [],
          // Contact information
          endereco: data.enderecos && data.enderecos.length > 0 ? {
            logradouro: data.enderecos[0].logradouro,
            numero: data.enderecos[0].numero,
            complemento: data.enderecos[0].complemento,
            bairro: data.enderecos[0].bairro,
            cidade: data.enderecos[0].cidade,
            estado: data.enderecos[0].uf,
            cep: data.enderecos[0].cep
          } : null,
          telefones: data.telefones ? data.telefones.map(tel => tel.telefoneComDDD).filter(Boolean) : [],
          telefones_completos: data.telefones || [],
          email: data.emails && data.emails.length > 0 ? data.emails[0].enderecoEmail : null,
          emails: data.emails || [],
          // Financial information
          renda_estimada: data.rendaEstimada,
          renda_faixa_salarial: data.rendaFaixaSalarial,
          // Additional metadata
          source: pessoaFisicaData.source,
          _original: data
        },
        message: 'Dados obtidos com sucesso'
      };
    }
    
    throw new Error('Dados não encontrados');

  } catch (error) {
    console.error('Erro ao consultar dados:', error);
    
    // Return mock data as fallback
    return {
      success: false,
      data: {
        nome_completo: advogadoData.nome,
        cpf: advogadoData.cpf,
        inscricoes: advogadoData.oab ? [{
          numero: advogadoData.oab.numero,
          uf: advogadoData.oab.uf,
          tipo: advogadoData.oab.tipo,
          situacao: 'ATIVO',
          data_inscricao: '2010-01-01'
        }] : [],
        endereco: {
          logradouro: 'Endereço não disponível',
          cidade: 'Salvador',
          estado: advogadoData.estado,
          cep: '00000-000'
        },
        telefones: [],
        email: null,
        sociedades: [],
        processos_disciplinares: 0,
        data_nascimento: null,
        nacionalidade: 'Brasileira',
        estado_civil: null
      },
      message: 'Erro na consulta - usando dados locais',
      error: error.message
    };
  }
};

/**
 * Get lawyer details combining local and CNA data
 * @param {Object} advogado - Local lawyer data
 * @param {String} estado - State code
 * @returns {Promise<Object>} Combined lawyer information
 */
export const getAdvogadoDetails = async (advogado, estado) => {
  try {
    const preparedData = prepareAdvogadoForCNA(advogado, estado);
    const cnaData = await fetchAdvogadoFromCNA(preparedData);
    
    // Combine local and CNA data
    return {
      ...advogado,
      cna: cnaData.data,
      cnaSuccess: cnaData.success,
      cnaMessage: cnaData.message
    };
  } catch (error) {
    console.error('Error fetching advogado details:', error);
    return {
      ...advogado,
      cnaSuccess: false,
      cnaError: error.message
    };
  }
};

/**
 * Check if a party is a lawyer
 * @param {Object} envolvido - Party information
 * @returns {Boolean} True if the party is a lawyer
 */
export const isAdvogado = (envolvido) => {
  return envolvido && (
    envolvido.polo === 'ADVOGADO' || 
    envolvido.tipo_normalizado === 'Advogado' ||
    (envolvido.tipo && envolvido.tipo.toLowerCase().includes('advogado')) ||
    (envolvido.tipo && envolvido.tipo.toLowerCase().includes('proc.'))
  );
};
